import React, { useState, useEffect } from 'react';
import { HiOutlineCalendar, HiOutlineLocationMarker, HiOutlineClock, HiOutlineDocumentText } from 'react-icons/hi';
import './CongressDates.css';
import { datesService } from '../services/datesService';
import { supabase } from '../supabaseClient';
import CountdownTimer from './CountdownTimer';

function CongressDates({ edition: customEdition }) {
  const [datesData, setDatesData] = useState({
    datesHeader: { title: 'Dates Importantes' },
    countdownInfo: { targetDate: '', targetTime: '09:00', isActive: true },
    congressMainInfo: { dateRange: '', location: '', subLocation: '' },
    extensionInfo: { title: '', deadline: '', description1: '', description2: '' },
    ctaInfo: { text: '', publicationNote: '' },
    keyDates: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDatesData();
  }, [customEdition]);

  const loadDatesData = async () => {
    try {
      setLoading(true);
      
      let editionYear = customEdition;
      if (!editionYear) {
        // Get active edition first
        const { data: activeEdition, error: editionError } = await supabase
          .from('editions')
          .select('year')
          .eq('is_active', true)
          .single();
        editionYear = activeEdition?.year || '2025';
      }

      const data = await datesService.getDatesData(editionYear);
      
      if (data) {
        setDatesData(data);
      }
      setError(null);
    } catch (error) {
      setError('Failed to load dates data');
    } finally {
      setLoading(false);
    }
  };

  // Use target date from data
  const targetDate = datesData.countdownInfo.targetDate && datesData.countdownInfo.targetTime 
    ? new Date(`${datesData.countdownInfo.targetDate}T${datesData.countdownInfo.targetTime}`)
    : new Date();

  const getIconComponent = (iconName) => {
    switch (iconName) {
      case 'HiOutlineLocationMarker':
        return <HiOutlineLocationMarker />;
      case 'HiOutlineCalendar':
        return <HiOutlineCalendar />;
      case 'HiOutlineClock':
        return <HiOutlineClock />;
      case 'HiOutlineDocumentText':
        return <HiOutlineDocumentText />;
      default:
        return <HiOutlineDocumentText />;
    }
  };

  if (loading) {
    return (
      <div className="congress-dates-page">
        <div className="congress-dates-container">
          <div className="dates-header">
           {/* Replace text with animated loading spinner */}
              <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="congress-dates-page">
      <div className="congress-dates-container">
        <div className="dates-header">
          <h1>{datesData.datesHeader.title}</h1>
        </div>

        <div className="dates-content">
          {/* Main Congress Info */}
          <div className="congress-info-section">
            <div className="event-details">
              <div className="details-grid">
                {[
                  {
                    id: 'Où ?',
                    icon: 'HiOutlineCalendar',
                    title: 'Où ?',
                    content: datesData.congressMainInfo.dateRange
                  },
                  {
                    id: 'Quand ?',
                    icon: 'HiOutlineLocationMarker',
                    title: 'Quand ?',
                    content: `${datesData.congressMainInfo.location}\n${datesData.congressMainInfo.subLocation}`
                  }
                ].map(detail => (
                  <div key={detail.id} className="detail-item">
                    <div className="detail-icon">
                      {getIconComponent(detail.icon)}
                    </div>
                    <div className="detail-content">
                      <h3>{detail.title}</h3>
                      {detail.content.split('\n').map((line, index) => (
                        <p key={index}>{line}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Extension Notice */}
          {datesData.extensionInfo.title && (
            <div className="extension-notice">
              <h2>{datesData.extensionInfo.title}</h2>
              <p className="deadline-text">{datesData.extensionInfo.deadline}</p>
              
              <div className="extension-description">
                <p>{datesData.extensionInfo.description1}</p>
                <p>{datesData.extensionInfo.description2}</p>
              </div>
            </div>
          )}

          {/* Countdown Timer */}
          {datesData.countdownInfo.isActive && (
            <div className="countdown-section">
              <h2>{datesData.countdownInfo.title}</h2>
              <CountdownTimer targetDate={targetDate} isActive={datesData.countdownInfo.isActive} />
            </div>
          )}

          {/* Key Dates */}
          {datesData.keyDates.length > 0 && (
            <div className="key-dates-section">
              <h2>Dates à retenir</h2>
              <div className="key-dates-grid">
                {datesData.keyDates.map((item) => (
                  <div key={item.id} className="key-date-item">
                    <div className="key-date-icon">
                      {getIconComponent(item.icon)}
                    </div>
                    <div className="key-date-content">
                      <h4>{item.title}</h4>
                      <p>{item.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Call to Action */}
          {(datesData.ctaInfo.text || datesData.ctaInfo.publicationNote) && (
            <div className="cta-section">
              {datesData.ctaInfo.text && (
                <p className="cta-text">{datesData.ctaInfo.text}</p>
              )}
              {datesData.ctaInfo.publicationNote && (
                <p className="publication-note">{datesData.ctaInfo.publicationNote}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CongressDates;

